import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

export type UserTier = 'Tier 1' | 'Tier 2' | 'Tier 3' | 'Tier 4';

interface UserRole {
  id: string;
  username: string;
  email: string;
  user_role: string;
  user_group: string;
  tier: UserTier;
}

interface UserRoleContextType {
  userRole: UserRole | null;
  loading: boolean;
  tier: UserTier | null;
  isTier4: boolean;
  isTier3: boolean;
  isTier2: boolean;
  isTier1: boolean;
  refreshUserRole: () => Promise<void>;
}

const UserRoleContext = createContext<UserRoleContextType>({
  userRole: null,
  loading: true,
  tier: null,
  isTier4: false,
  isTier3: false,
  isTier2: false,
  isTier1: false,
  refreshUserRole: async () => {},
});

export function UserRoleProvider({ children }: { children: ReactNode }) {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const mapUserGroupToTier = (userGroup: string): UserTier => {
    switch (userGroup) {
      case 'OS Team':
        return 'Tier 1';
      case 'Show Management (Bay)':
      case 'Show Management (Padang)':
        return 'Tier 2';
      case 'Show Support':
      case 'Artiste Management':
        return 'Tier 3';
      case 'Log & Finance':
      case 'Ops. Safety and Contingencies':
      default:
        return 'Tier 4';
    }
  };

  const fetchUserRole = async () => {
    if (!user) {
      setUserRole(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, email, user_role, user_group')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        const tier = mapUserGroupToTier(data.user_group);
        setUserRole({
          ...data,
          tier,
        });
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserRole();
  }, [user]);

  const refreshUserRole = async () => {
    setLoading(true);
    await fetchUserRole();
  };

  const value = {
    userRole,
    loading,
    tier: userRole?.tier || null,
    isTier4: userRole?.tier === 'Tier 4',
    isTier3: userRole?.tier === 'Tier 3',
    isTier2: userRole?.tier === 'Tier 2',
    isTier1: userRole?.tier === 'Tier 1',
    refreshUserRole,
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
}

export const useUserRole = () => {
  return useContext(UserRoleContext);
}; 